import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { publicApi, imageUrl } from '../api';

export default function Gallery() {
  const [active, setActive] = useState(null);
  const [galleryImages, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.getAlbum('gallery').then(setImages).catch(() => setImages([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (active === null) return;
    const n = galleryImages.length;
    const onKey = (e) => {
      if (e.key === 'Escape') setActive(null);
      if (e.key === 'ArrowRight') setActive((v) => (v + 1) % n);
      if (e.key === 'ArrowLeft') setActive((v) => (v - 1 + n) % n);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, galleryImages.length]);

  const next = () => setActive((v) => (v + 1) % galleryImages.length);
  const prev = () => setActive((v) => (v - 1 + galleryImages.length) % galleryImages.length);

  return (
    <div className="bg-[#faf6f0] pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <span className="text-amber-600 tracking-[0.3em] text-xs uppercase">Einblicke</span>
          <h1 className="font-serif text-5xl md:text-6xl text-[#1a1410] mt-4">Galerie</h1>
          <div className="w-16 h-[3px] bg-amber-500 mx-auto mt-6"/>
          <p className="text-[#5a4a3d] max-w-2xl mx-auto mt-6">Ein kleiner Einblick in unsere Speisen, unser Lokal und unsere Events.</p>
        </div>

        {loading && <div className="text-center text-[#5a4a3d] py-10">Lädt …</div>}
        {!loading && galleryImages.length === 0 && (
          <div className="bg-white rounded-2xl border border-amber-100 p-12 text-center" data-testid="gallery-empty">
            <ImageIcon size={36} className="text-amber-500 mx-auto mb-4"/>
            <p className="text-[#5a4a3d]">Bald finden Sie hier neue Bilder.</p>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
          {galleryImages.map((g, i) => (
            <button key={g.id} data-testid={`gallery-item-${i}`} onClick={() => setActive(i)} className="relative group aspect-square rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-amber-500">
              <img src={imageUrl(g.image_url)} alt={g.title || 'Galeriebild'} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <span className="text-white font-serif text-sm text-left">{g.title}</span>
              </div>
            </button>
          ))}
        </div>

        {active !== null && (
          <div className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4" onClick={() => setActive(null)}>
            <button data-testid="lightbox-close" onClick={(e) => { e.stopPropagation(); setActive(null); }} className="absolute top-6 right-6 text-white p-2 hover:text-amber-400"><X size={28}/></button>
            <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 md:left-10 text-white p-3 hover:text-amber-400"><ChevronLeft size={36}/></button>
            <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={imageUrl(galleryImages[active].image_url)} alt={galleryImages[active].title || 'Galeriebild'} className="w-full max-h-[80vh] object-contain rounded-lg"/>
              <div className="text-center text-white/90 mt-4 font-serif text-lg">{galleryImages[active].title}</div>
              <div className="text-center text-white/50 text-sm mt-1">{active + 1} / {galleryImages.length}</div>
            </div>
            <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 md:right-10 text-white p-3 hover:text-amber-400"><ChevronRight size={36}/></button>
          </div>
        )}
      </div>
    </div>
  );
}
